import React from "react";
import { isLikelyBenignRemoveChildError } from "@/lib/benign-dom-errors";
import { ErrorComponent } from "@/components/ui/ErrorComponent";
import i18n from "@/i18n";

let benignDomRecoverGeneration = 0;

type State = {
  recoverKey: number;
  lastRealError: Error | null;
};

export class RootRecoverableErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { recoverKey: 0, lastRealError: null };

  static getDerivedStateFromError(error: unknown): Partial<State> | null {
    if (isLikelyBenignRemoveChildError(error)) {
      benignDomRecoverGeneration += 1;
      return {
        recoverKey: benignDomRecoverGeneration,
        lastRealError: null,
      };
    }
    return {
      lastRealError: coerceToError(error),
    };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    if (isLikelyBenignRemoveChildError(error)) {
      if (import.meta.env.DEV) {
        console.warn(
          "[app] Recovered from benign DOM error (often extensions or portal timing).",
          error,
          errorInfo.componentStack
        );
      }
      return;
    }
    console.error(error, errorInfo.componentStack);
  }

  render() {
    if (this.state.lastRealError) {
      return <ErrorComponent error={this.state.lastRealError} />;
    }

    return (
      <React.Fragment key={this.state.recoverKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

function coerceToError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error(i18n.t("error.unexpected"));
  }
}
